/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { HeroModule, isInvertedColor, toLayoutValue } from '@coursera-web/cds-labs';

import { Button, breakpoints, useMediaQuery } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';
import type { ButtonName } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { useCampaignModulesData } from 'bundles/campaigns/context/CampaignModulesDataContext';

type HeroModuleProps = React.ComponentProps<typeof HeroModule>;

const styles = {
  container: css`
    ${breakpoints.down('xs')} {
      text-align: center;
    }
  `,
  heading: css`
    ${breakpoints.down('xs')} {
      text-align: center;
    }
  `,
  description: css`
    margin-bottom: var(--cds-spacing-200);
    white-space: pre-line;

    ${breakpoints.up('md')} {
      width: 80%;
    }
  `,
  button: css`
    && {
      text-decoration: none;
    }

    ${breakpoints.down('xs')} {
      width: 100%;
    }
  `,
};

export const useShouldShowCampaignHeroModule = () => {
  const { campaignData, loading } = useCampaignModulesData();
  return !!campaignData?.heroModule?.contentData || loading;
};

const CampaignHeroModuleRenderer = () => {
  const track = useTracker();
  const isMobile = useMediaQuery(breakpoints.down('xs'));
  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_page_section',
    {
      pageSection: {
        sectionName: 'hero_banner',
      },
    }
  );

  const content = useCampaignModulesData()?.campaignData?.heroModule?.contentData;

  if (!useShouldShowCampaignHeroModule()) {
    return null;
  }

  const {
    description,
    backgroundColor,
    ctaText,
    ctaTarget,
    headline,
    secondaryCtaTarget,
    secondaryCtaText,
    layout,
    eyebrow,
    eyebrowBackground,
    eyebrowImage,
  } = content || {};

  const trackClick = (buttonName: ButtonName, linkName: string, linkTarget: string) => () => {
    track('click_button', {
      button: { name: buttonName, linkName, linkURL: linkTarget, linkType: 'internal' },
      pageSection: {
        sectionName: 'hero_banner',
      },
    });
  };

  const descriptionToShow = (descriptionCopy?: string | null) => (
    <p css={styles.description}>{descriptionCopy ?? ''}</p>
  );

  const titleToShow = (titleCopy: string) => <div css={styles.heading}>{titleCopy}</div>;

  const isDarkBackground = isInvertedColor(backgroundColor ?? '');

  const getHeroModuleProps = (): HeroModuleProps => {
    return {
      ...content,
      eyebrow: {
        label: eyebrow,
        image: { ...eyebrowImage, ...(eyebrowImage?.height && isMobile ? { height: eyebrowImage.height * 0.75 } : {}) },
        backgroundColor: eyebrowBackground,
      },
      headline: titleToShow(headline ?? ''),
      showImagesAboveContentOnMobile: false,
      description: descriptionToShow(description),
      layout: toLayoutValue(layout),
      preloadImage: true,
      customPrimaryCta:
        ctaText && ctaTarget ? (
          <Button
            variant={isDarkBackground ? 'secondary' : 'primary'}
            component="a"
            href={ctaTarget}
            icon={<ArrowNextIcon />}
            iconPosition="after"
            css={styles.button}
            onClick={trackClick('hero_primary_cta', ctaText, ctaTarget)}
          >
            {ctaText}
          </Button>
        ) : undefined,
      customSecondaryCta:
        secondaryCtaText && secondaryCtaTarget ? (
          <Button
            variant={isDarkBackground ? 'primary' : 'secondary'}
            component="a"
            href={secondaryCtaTarget}
            icon={<ArrowNextIcon />}
            iconPosition="after"
            css={styles.button}
            onClick={trackClick('hero_secondary_cta', secondaryCtaText, secondaryCtaTarget)}
          >
            {secondaryCtaText}
          </Button>
        ) : undefined,
    };
  };

  return (
    <div css={styles.container} ref={visibilityTrackingRef}>
      <HeroModule {...getHeroModuleProps()} />
    </div>
  );
};

export default CampaignHeroModuleRenderer;
